import type {
  CreateCustomerExpenseInput,
  CustomerExpense,
  PatchCustomerExpenseInput,
} from '@clockwise/clients/customer-expenses';
import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import { ApiError, isoDateString } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customerExpensesApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { localTodayIso } from '@/lib/utils';
import { ExpenseCatalogAutocomplete } from './expense-catalog-autocomplete';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** When set, the sheet edits this line. When null, it adds one. Add and
   *  edit are the SAME screen deliberately: a line is described by its item,
   *  its wording and its amount together, so the form that creates one shows
   *  the same fields the one that changes it does. */
  expense?: CustomerExpense | null;
}

/** Plain non-negative decimal, at most two fractional digits. Same rule as
 *  `ExpenseCatalogFormSchema`, and as the server's `costAmount`. */
const COST_REGEX = /^\d+(\.\d{1,2})?$/;

/** Digits available left of the decimal point in `numeric(12, 2)`. */
const COST_MAX_WHOLE_DIGITS = 10;

/**
 * Client-side rules are deliberately identical to the server's, so a violation
 * surfaces inline under the offending input instead of arriving as a 400 the
 * user has to decode (G12).
 *
 * `cost` is held as a STRING, never a number. `<input>` hands back a string
 * anyway, and keeping it one all the way to the API means the amount never
 * round-trips through a binary float — and lets a third decimal place be
 * rejected rather than silently rounded by Postgres.
 */
const FormSchema = z
  .object({
    expenseCatalogId: z.string().uuid('Select an expense item'),
    description: z.string().trim().min(1, 'Required').max(500, 'Up to 500 characters'),
    /**
     * Blank is legal HERE and rejected by the form's own check below unless
     * the picked catalog item is variable — a rule this schema cannot express
     * on its own, because whether a line may carry an amount is a property of
     * the item, which lives in a query result rather than in the form.
     */
    cost: z
      .string()
      .regex(COST_REGEX, 'Enter a non-negative amount with at most 2 decimal places')
      .refine(
        (v) => (v.split('.')[0] ?? '').replace(/^0+(?=\d)/, '').length <= COST_MAX_WHOLE_DIGITS,
        `Must be less than ${'9'.repeat(COST_MAX_WHOLE_DIGITS)}`,
      )
      .or(z.literal('')),
    effectiveFrom: isoDateString('Required'),
    effectiveTo: isoDateString().or(z.literal('')),
  })
  .refine((v) => v.effectiveTo === '' || v.effectiveTo > v.effectiveFrom, {
    message: 'End must be after start',
    path: ['effectiveTo'],
  });
type FormValues = z.infer<typeof FormSchema>;

const emptyDefaults = (): FormValues => ({
  expenseCatalogId: '',
  description: '',
  cost: '',
  effectiveFrom: localTodayIso(),
  effectiveTo: '',
});

/** Server field paths that map 1:1 onto a form field. */
const FORM_FIELDS = [
  'expenseCatalogId',
  'description',
  'cost',
  'effectiveFrom',
  'effectiveTo',
] as const;
const isFormField = (path: string): path is (typeof FORM_FIELDS)[number] =>
  (FORM_FIELDS as readonly string[]).includes(path);

/**
 * Add or edit ONE pass-through expense line — the only path to either.
 *
 * Adding is deliberately a single step. It used to take two: a multi-select
 * catalog picker created lines at their catalog price, and anything charged at
 * a different amount then had to be found in the table and edited. Nothing was
 * gained by splitting them — the same catalog item can be billed to a customer
 * more than once (one line per subsidiary), so picking an item never settled
 * anything a later step could rely on. The item, the wording and the amount are
 * one decision, so they are collected on one screen.
 *
 * Selecting a catalog item prefills `description` and `cost` from the item's
 * current values, and both stay editable — the prefill is a starting point, not
 * a binding. That prefill happens HERE, where the user can see the fields
 * change, rather than server-side on a repoint, which would silently discard an
 * override they had set earlier.
 *
 * Prefill deliberately does NOT clobber values the user has already touched: it
 * only writes into a field the form still considers pristine. Otherwise
 * changing your mind about the item after typing a negotiated price would throw
 * that price away.
 */
export function CustomerExpenseFormSheet({ open, onOpenChange, customerId, expense }: Props) {
  const queryClient = useQueryClient();
  const isEdit = expense != null;

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: emptyDefaults(),
  });

  // Reset once per open (and once per target line). Keyed rather than
  // dependency-driven so a background refetch can't wipe out a half-typed
  // amount.
  const initKey = `${expense?.id ?? 'new'}:${expense?.updatedAt ?? ''}`;
  const initializedFor = useRef<string | null>(null);
  /** Exactly what a catalog pick last wrote into each field, so a later pick
   *  can tell its own prefill apart from a stored or hand-typed value. */
  const autofilled = useRef<{ description?: string; cost?: string }>({});
  /**
   * The catalog item currently selected, as the picker last reported it.
   *
   * Needed because whether this line may carry an amount is a property of the
   * ITEM, and the form only holds its id. Seeded from the line being edited
   * via `variable` on the stored row's own cost — a null cost is the shape a
   * variable line has — so the Cost field is right on first paint, before the
   * picker has resolved anything.
   */
  const [pickedItem, setPickedItem] = useState<ExpenseCatalog | null>(null);
  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    if (initializedFor.current === initKey) return;
    initializedFor.current = initKey;
    // Seeding from a stored line means NOTHING on this form is autofill —
    // those values are what the customer has agreed, however they got there.
    autofilled.current = {};
    setPickedItem(null);
    form.reset(
      expense
        ? {
            expenseCatalogId: expense.expenseCatalogId,
            description: expense.description,
            // A stored line with no cost is a variable one; '' keeps the input
            // controlled and is what the schema expects in that case.
            cost: expense.cost ?? '',
            effectiveFrom: expense.effectiveFrom,
            effectiveTo: expense.effectiveTo ?? '',
          }
        : emptyDefaults(),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initKey]);

  const onCatalogChange = (item: ExpenseCatalog | null) => {
    setPickedItem(item);
    form.setValue('expenseCatalogId', item?.id ?? '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (!item) return;
    if (item.variable) {
      // A variable item has no price, and this line may not carry one — the
      // API rejects a cost against it outright. Clear whatever is in the box
      // (a leftover prefill from a previously picked fixed item) so a switch
      // between the two can't submit an amount the server will refuse.
      form.setValue('cost', '', { shouldValidate: true });
      autofilled.current.cost = '';
    }
    // Replace a field only if it is still exactly what a previous pick put
    // there (or is empty). `dirtyFields` is NOT sufficient: after `reset()`
    // seeds an existing line it is empty, so an agreed price the user never
    // retyped would read as "untouched default" and be silently overwritten
    // with the new item's catalog price — real money changing because someone
    // corrected which item a line points at.
    //
    // Tracking what WE wrote distinguishes the two: in add mode, switching
    // between items keeps re-prefilling as you'd expect; in edit mode, the
    // stored values are never autofill and so are never clobbered.
    const current = form.getValues();
    if (!current.description || current.description === autofilled.current.description) {
      form.setValue('description', item.description, { shouldValidate: true });
      autofilled.current.description = item.description;
    }
    if (item.cost !== null && (!current.cost || current.cost === autofilled.current.cost)) {
      form.setValue('cost', item.cost, { shouldValidate: true });
      autofilled.current.cost = item.cost;
    }
  };

  /**
   * Whether the line being edited may carry an amount.
   *
   * Read off the picked item when there is one. In edit mode before the user
   * touches the picker there isn't, so fall back to the stored line's own
   * shape: a null cost is exactly what a variable line looks like, which makes
   * the Cost field correct on first paint without waiting on a second fetch
   * for an item the form already implies.
   */
  const isVariable = pickedItem ? pickedItem.variable : expense != null && expense.cost === null;

  const mutation = useMutation({
    mutationFn: async ({ values: v }: { values: FormValues; addAnother: boolean }) => {
      const effectiveTo = v.effectiveTo === '' ? null : v.effectiveTo;
      // Against a variable item the key is OMITTED, not sent as '' or null:
      // the API rejects any cost on such a line, and an absent key is the only
      // spelling it reads as "this line has no amount". The server clears any
      // stored one itself when the item is variable, so a patch needs to say
      // nothing.
      const cost = isVariable ? undefined : v.cost;
      if (isEdit && expense) {
        const payload: PatchCustomerExpenseInput = {
          expenseCatalogId: v.expenseCatalogId,
          description: v.description,
          cost,
          effectiveFrom: v.effectiveFrom,
          effectiveTo,
        };
        return customerExpensesApi.patch(expense.id, payload);
      }
      const payload: CreateCustomerExpenseInput = {
        expenseCatalogId: v.expenseCatalogId,
        description: v.description,
        cost,
        effectiveFrom: v.effectiveFrom,
        effectiveTo,
      };
      return customerExpensesApi.create(customerId, payload);
    },
    onSuccess: (_data, { addAnother }) => {
      toast.success(isEdit ? 'Expense updated' : 'Expense added');
      queryClient.invalidateQueries({ queryKey: queryKeys.customerExpenses.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      if (addAnother) {
        // Setting a customer up means entering several lines in a row, which
        // is the one thing the old bulk picker was good at. Keep the sheet
        // open and clear the line just saved — but keep the START date, since
        // consecutive lines almost always begin on the same day and retyping
        // it each time is the cost this is meant to avoid.
        //
        // `effectiveTo` is deliberately NOT carried over. Blank means ongoing,
        // which is the safe default for a line nobody has said anything about
        // yet; inheriting the end date of a fixed-term line would silently
        // stop billing the next one on a date the user chose for something
        // else, and they would not find out until an invoice came up short.
        //
        // Nothing on the cleared form is autofill any more, so a fresh catalog
        // pick is free to prefill both fields again. `pickedItem` has to go
        // with it: the cleared form has no item selected, and leaving the last
        // one behind keeps `isVariable` describing a line that is no longer on
        // screen — hiding Cost and claiming "this item's amount varies" for a
        // line the user has not chosen an item for yet.
        const { effectiveFrom } = form.getValues();
        autofilled.current = {};
        setPickedItem(null);
        form.reset({ ...emptyDefaults(), effectiveFrom });
        // Deliberately does NOT focus the item picker: focus opens its
        // popover, which would cover the form the user is looking at — the
        // same reason `onOpenAutoFocus` is prevented below.
        return;
      }
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        let matched = false;
        for (const fe of err.fields) {
          if (isFormField(fe.path)) {
            form.setError(fe.path, { message: fe.message });
            matched = true;
          }
        }
        if (matched) {
          toast.error('Please fix the highlighted fields');
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const submit = (addAnother: boolean) =>
    form.handleSubmit((v) => {
      // "Cost is required" can't live in the Zod schema: whether this line may
      // carry an amount depends on the picked catalog item, which is not a
      // form field. Checking it here keeps the message inline under the input
      // rather than arriving as a server 400 the user has to decode (G12).
      if (!isVariable && v.cost === '') {
        form.setError('cost', { message: 'Required' });
        return;
      }
      mutation.mutate({ values: v, addAnother });
    })();
  const errors = form.formState.errors;

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent
          className="flex flex-col gap-0 sm:max-w-lg"
          // Don't autofocus: the first control is the catalog picker, and
          // opening its popover on mount would cover the form.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit expense' : 'Add expense'}</SheetTitle>
            <SheetDescription>
              {isEdit
                ? 'Update what this customer is billed for this item.'
                : 'Pick a catalog item; its description and price are copied here and can be adjusted for this customer.'}
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit(false);
            }}
            className="flex min-h-0 flex-1 flex-col"
          >
            {/* Every field is locked while a save is in flight, not just the
                buttons. "Save and add another" leaves the sheet open and
                resets the form when the create lands, so an editable field
                during that window is one whose contents are about to be
                thrown away without a trace — a user who clicks save and
                immediately types the next line's description would watch it
                vanish a second later. Closing the window beats reconciling
                the edit afterwards. */}
            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
              <div className="space-y-1.5">
                <Label htmlFor="pte-item">Expense item</Label>
                <ExpenseCatalogAutocomplete
                  id="pte-item"
                  value={form.watch('expenseCatalogId') || null}
                  onChange={onCatalogChange}
                  clearable={false}
                  disabled={mutation.isPending}
                />
                {errors.expenseCatalogId ? (
                  <p className="text-xs text-destructive">{errors.expenseCatalogId.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    The catalog price is copied here, not linked. Repricing the catalog later won’t
                    change this line.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="pte-description">Description</Label>
                <Input
                  id="pte-description"
                  {...form.register('description')}
                  aria-invalid={!!errors.description}
                  disabled={mutation.isPending}
                />
                {errors.description ? (
                  <p className="text-xs text-destructive">{errors.description.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    This is what appears on the invoice.
                  </p>
                )}
              </div>

              {/* Replaced, not disabled, for a variable item: a greyed-out
                  money box reads as "an amount belongs here and you may not
                  set it". The truth is that no amount belongs on this line at
                  all, which takes a sentence rather than a dimmed input. */}
              {isVariable ? (
                <div className="space-y-1.5">
                  <Label>Cost</Label>
                  <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
                    This item's amount varies, so this line carries no standing price and will not
                    be added to a generated invoice automatically.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="pte-cost">Cost</Label>
                  {/* type="text" + inputMode="decimal", NOT type="number". A
                      number input hands validation to the browser, so `90.005`
                      becomes a stepMismatch the browser blocks with its own
                      tooltip — react-hook-form never runs and the inline "at
                      most 2 decimal places" message is unreachable. */}
                  <Input
                    id="pte-cost"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    className="text-right tabular-nums"
                    {...form.register('cost')}
                    aria-invalid={!!errors.cost}
                    disabled={mutation.isPending}
                  />
                  {errors.cost ? (
                    <p className="text-xs text-destructive">{errors.cost.message}</p>
                  ) : null}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="pte-from">Effective from</Label>
                  <Input
                    id="pte-from"
                    type="date"
                    {...form.register('effectiveFrom')}
                    aria-invalid={!!errors.effectiveFrom}
                    disabled={mutation.isPending}
                  />
                  {errors.effectiveFrom ? (
                    <p className="text-xs text-destructive">{errors.effectiveFrom.message}</p>
                  ) : null}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="pte-to">Effective to</Label>
                  <Input
                    id="pte-to"
                    type="date"
                    {...form.register('effectiveTo')}
                    aria-invalid={!!errors.effectiveTo}
                    disabled={mutation.isPending}
                  />
                  {errors.effectiveTo ? (
                    <p className="text-xs text-destructive">{errors.effectiveTo.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Blank = ongoing.</p>
                  )}
                </div>
              </div>
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              {/* Add mode only. Editing an existing line has nothing to
                  chain onto, and a "save and add another" there would quietly
                  turn a correction into a new charge. */}
              {isEdit ? null : (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={mutation.isPending}
                  onClick={() => void submit(true)}
                >
                  Save and add another
                </Button>
              )}
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Add expense'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
